import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";
import React, { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Schedule } from "@mui/icons-material";
import { DrivingTimeSimpleDisplay } from "../../../drivingTimes/DrivingTimesSimpleDisplay";

export const DriveTimeLogPopover: React.FC<{ employeeId: string }> = ({
    employeeId,
}) => {
    const popoverAnchor = useRef(null);
    const popoverPaperRef = useRef<HTMLDivElement | null>(null);
    const popoverHeightRef = useRef<number>(220);
    const openUpwardRef = useRef(false);
    const [openedPopover, setOpenedPopover] = useState(false);
    const [openUpward, setOpenUpward] = useState(false);

    const handlePopoverOpen = useCallback(() => {
        if (popoverAnchor.current) {
            const rect = (
                popoverAnchor.current as HTMLElement
            ).getBoundingClientRect();
            // Pre-calculate direction using last known popover height
            const shouldOpenUpward =
                rect.bottom + popoverHeightRef.current > window.innerHeight;
            openUpwardRef.current = shouldOpenUpward;
            setOpenUpward(shouldOpenUpward);
        }
        setOpenedPopover(true);
    }, []);
    const handlePopoverClose = useCallback(() => setOpenedPopover(false), []);

    // After render, correct direction if popover is actually overflowing below viewport.
    // useLayoutEffect runs synchronously before browser paint so there is no visible flicker.
    useLayoutEffect(() => {
        if (!openedPopover || !popoverPaperRef.current) return;
        const paperRect = popoverPaperRef.current.getBoundingClientRect();
        // Update stored height for better pre-calculation on future opens
        if (paperRect.height > 0) popoverHeightRef.current = paperRect.height;
        // Flip upward if the popover is actually overflowing below the viewport
        if (paperRect.bottom > window.innerHeight && !openUpwardRef.current) {
            openUpwardRef.current = true;
            setOpenUpward(true);
        }
    }, [openedPopover]);

    return (
        <Box sx={{ width: "15px", display: "inline-block", mr: 0.5 }}>
            <Typography
                ref={popoverAnchor}
                aria-owns={openedPopover ? "mouse-over-popover" : undefined}
                aria-haspopup="true"
                onMouseOver={handlePopoverOpen}
                onMouseLeave={handlePopoverClose}
                onClick={(e) => {
                    e.stopPropagation();
                    handlePopoverOpen();
                }}
                sx={{ marginRight: 1 }}
            >
                <Schedule fontSize="small" color="primary" />
            </Typography>
            <Popover
                id="mouse-over-popover"
                open={openedPopover}
                anchorEl={popoverAnchor.current}
                onClose={handlePopoverClose}
                anchorOrigin={{
                    vertical: openUpward ? "top" : "bottom",
                    horizontal: "left",
                }}
                transformOrigin={{
                    vertical: openUpward ? "bottom" : "top",
                    horizontal: "left",
                }}
                onMouseEnter={() => setOpenedPopover(true)}
                onMouseLeave={handlePopoverClose}
                slotProps={{
                    paper: {
                        ref: (el: HTMLDivElement | null) => {
                            popoverPaperRef.current = el;
                        },
                        sx: { pointerEvents: "auto !important" }, // paper properly opens and closes on hover
                        onClick: (e) => {
                            e.stopPropagation();
                        }, // prevent click event from propagating to the parent
                    },
                }}
                sx={{ pointerEvents: "none" }}
            >
                <Box sx={{ p: 0 }}>
                    <DrivingTimeSimpleDisplay employeeId={employeeId} />
                </Box>
            </Popover>
        </Box>
    );
};
